import React from 'react'
import { useAppContext } from '../../../providers/context'
import { Button } from '../../../components/simple/button'
import { BlueInput, TextInput } from '../../../components/form/textInput'
import { wrapOptionalSetState } from '../../../utils'
import { sendComposedMessage } from './send'
import { showAxiosErrorAsToast } from '../../../request'
import { ScheduledTimePicker, ScheduleTimeInstructions } from './scheduledTimePicker'
import { HoverPopover, Popover } from '../../../layouts/components/popover'
import { Divider } from '../../../components/simple/divider'
import { Select } from '../../../components/form/select'
import { Toasts } from '../../../providers/toast'
import { DateTimePicker } from '../../../components/form/timePicker'
import { ComposeState } from './composeTypes'
import { FloatingLoader } from '../../../components/simple/loader'
import { IconQuestionCircle } from '../../../icons/library'
import { useLanguages } from '../../../providers/languages'

export const SendButtonPopover: React.FC<{
  children: React.ReactNode
  composeState: ComposeState
  setComposeState: React.Dispatch<React.SetStateAction<ComposeState | undefined>>
  showSendLaterCalendar: boolean
  showSchedulingCalendar: boolean
  showTestMessagePopup: boolean
  setShowSendLaterCalendar: React.Dispatch<React.SetStateAction<boolean>>
  setShowSchedulingCalendar: React.Dispatch<React.SetStateAction<boolean>>
  setShowTestMessagePopup: React.Dispatch<React.SetStateAction<boolean>>
  sendMessage: (composeState: ComposeState) => void
}> = ({
  children,
  composeState,
  setComposeState,
  showSendLaterCalendar,
  showSchedulingCalendar,
  showTestMessagePopup,
  setShowSendLaterCalendar,
  setShowSchedulingCalendar,
  setShowTestMessagePopup,
  sendMessage,
}) => {
  const closePopover = () => {
    setShowSendLaterCalendar(false)
    setShowSchedulingCalendar(false)
    setShowTestMessagePopup(false)
  }
  return (
    <Popover
      open={showSendLaterCalendar || showSchedulingCalendar || showTestMessagePopup}
      onOpenChange={(open) => {
        if (!open) closePopover()
      }}
      side="bottom"
      className="items-center gap-2 p-2"
      trigger={children}
    >
      {showSendLaterCalendar && (
        <>
          <div className="text-lg font-semibold">Choose when to send</div>
          <DateTimePicker
            minDate={new Date()}
            dateTime={composeState.sendLaterDate}
            setDateTime={wrapOptionalSetState(setComposeState, 'sendLaterDate')}
          />
          {composeState.sendLaterDate && (
            <Button logo onClick={() => sendMessage(composeState)}>
              Send
            </Button>
          )}
        </>
      )}
      {showSchedulingCalendar && (
        <ScheduleMessageForm
          composeState={composeState}
          setComposeState={setComposeState}
          sendMessage={sendMessage}
          closePopover={closePopover}
        />
      )}
      {showTestMessagePopup && (
        <TestMessageForm composeState={composeState} setShowTestMessagePopup={setShowTestMessagePopup} />
      )}
    </Popover>
  )
}

const TestMessageForm: React.FC<{
  composeState: ComposeState
  setShowTestMessagePopup: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ composeState, setShowTestMessagePopup }) => {
  const { state } = useAppContext()
  const user = state.user!
  const [email, setEmail] = React.useState(user.email)
  const [phone, setPhone] = React.useState(user.phoneNumber || '')
  const [emailInvalid, setEmailInvalid] = React.useState(false)
  const [phoneInvalid, setPhoneInvalid] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  const languages = useLanguages()
  const languageOptions = React.useMemo(
    () => languages.getOptions((l) => l.googleTranslatable || l.code === 'en'),
    [languages]
  )
  const [language, setLanguage] = React.useState(languageOptions.find((o) => o.value === 'en')!)

  const showEmail = composeState.sendTypes.has('email')
  const showPhone = composeState.sendTypes.has('text') || composeState.sendTypes.has('phone')

  const sendTest = () => {
    setLoading(true)
    setEmailInvalid(false)
    setPhoneInvalid(false)
    sendComposedMessage({
      ...composeState,
      testUser: {
        email: showEmail ? email : '',
        phoneNumber: showPhone ? phone : '',
        language: language.value,
      },
    })
      .then((response) => {
        if (response === 'invalid-email') {
          setEmailInvalid(true)
        } else if (response === 'invalid-phone') {
          setPhoneInvalid(true)
        } else {
          Toasts.showToast({ message: response })
          setShowTestMessagePopup(false)
        }
      })
      .catch(showAxiosErrorAsToast)
      .finally(() => setLoading(false))
  }

  return (
    <>
      {loading && <FloatingLoader background="bg-white/50" />}
      <div className="font-semibold">Send a test</div>
      <div className="max-w-72 text-sm">
        This message will <b>only go to the contact listed below</b>, and not to any selected recipients. The 'New
        Message' window will not be closePopoverd.
      </div>
      {showEmail && (
        <TextInput
          className={`self-stretch ${BlueInput}`}
          placeholder="Email address"
          value={email}
          onChange={setEmail}
          invalid={emailInvalid}
          validation="email"
        />
      )}
      {showPhone && (
        <TextInput
          className={`self-stretch ${BlueInput}`}
          placeholder="Phone number"
          value={phone}
          onChange={setPhone}
          invalid={phoneInvalid}
          validation="phone"
        />
      )}
      <Select
        className={`self-stretch ${BlueInput}`}
        options={languageOptions}
        value={language}
        onChange={setLanguage}
      />
      <Button logo onClick={sendTest}>
        Send
      </Button>
    </>
  )
}

const ScheduleMessageForm: React.FC<{
  composeState: ComposeState
  setComposeState: React.Dispatch<React.SetStateAction<ComposeState | undefined>>
  sendMessage: (composeState: ComposeState) => void
  closePopover: () => void
}> = ({ composeState, setComposeState, sendMessage, closePopover }) => {
  if (composeState.callSuggestingTimes === undefined) {
    return (
      <div className="flex max-w-72 flex-col gap-2">
        Would you like to schedule a single time or offer multiple times for the recipient to pick from?
        <Button onClick={() => setComposeState((cs) => cs && { ...cs, callSuggestingTimes: false })}>
          Schedule a single time
        </Button>
        <Button onClick={() => setComposeState((cs) => cs && { ...cs, callSuggestingTimes: true })}>
          Offer multiple times
        </Button>
      </div>
    )
  }

  return (
    <>
      <ScheduledTimePicker
        scheduledTimes={composeState.scheduledTimes}
        setScheduledTimes={wrapOptionalSetState(setComposeState, 'scheduledTimes')}
        note={composeState.scheduledTimeNote || ''}
        setNote={wrapOptionalSetState(setComposeState, 'scheduledTimeNote')}
        lexikeet={composeState.sendOption.type === 'schedule-interpreted'}
        singleTime={composeState.callSuggestingTimes === false}
      />
      {composeState.callSuggestingTimes && <Divider />}
      <div className="flex items-center justify-between gap-2 self-stretch">
        <Button
          small
          onClick={() =>
            setComposeState(
              (cs) => cs && { ...cs, callSuggestingTimes: undefined, scheduledTimes: [], scheduledTimeNote: undefined }
            )
          }
        >
          Reset
        </Button>
        {composeState.callSuggestingTimes ? (
          <>
            <Button
              logo
              disabled={!composeState.scheduledTimes || composeState.scheduledTimes.length === 0}
              onClick={() => {
                sendMessage(composeState)
                closePopover()
              }}
            >
              Suggest these times
            </Button>
            <HoverPopover trigger={<IconQuestionCircle size="lg" className="text-slate-600" />}>
              <ScheduleTimeInstructions />
            </HoverPopover>
          </>
        ) : (
          <Button
            logo
            disabled={!composeState.scheduledTimes || composeState.scheduledTimes.length === 0}
            onClick={() => {
              sendMessage(composeState)
              closePopover()
            }}
          >
            Schedule
          </Button>
        )}
      </div>
    </>
  )
}
